import { useEffect, useMemo, useRef, useState } from "react";
import { ModusOrb } from "./ModusOrb/ModusOrb";
import { useModus } from "../../hooks/use-modus";
import {
  ActionButton,
  RecordingObjectType,
  TypeOrRecord,
  useDatadog,
  useMicRecorder
} from "../../..";
import { Button, Tooltip } from "antd";
import {
  BsVolumeMuteFill,
  BsVolumeUpFill,
  BsEyeFill,
  BsEyeSlashFill
} from "react-icons/bs";
import { IoMdAdd } from "react-icons/io";
import styles from "./Converse.module.css";
import ReactMarkdown from "react-markdown";
import handPaintedArrowShort from "../../static/hand-painted-arrow-short.svg";
import arrowUp3 from "../../static/arrow-up-3.svg";
import { FaHeart } from "react-icons/fa";

export const Converse = ({
  size = 750,
  onProcess,
  showTranscriptIcon,
  style,
  fontSizeOverride,
  orbFontSizeOverride,
  showSquigly = false,
  squiglySize = "large",
  placeHolder = null,
  showNewConversationButton = false,
  headerActionButtons = []
}: {
  size?: number;
  onProcess: (transcript: string, _: string) => Promise<void>;
  showTranscriptIcon?: boolean;
  style?: React.CSSProperties;
  fontSizeOverride?: string;
  orbFontSizeOverride?: string;
  // textAlignOverride?: string;
  // justifyContentOverride?: string;
  // showVoMessage?: boolean;
  showSquigly?: boolean;
  squiglySize?: "large" | "small";
  placeHolder?: string | null;
  showNewConversationButton?: boolean;
  headerActionButtons?: { text: string; onClick: () => void }[];
}) => {
  const {
    modusChatContext,
    isWaitingForResponse,
    isAudioMuted,
    setIsAudioMuted,
    latestMessage,
    latestUserMessage,
    resetChatContext,
    latestVoMessage
  } = useModus();

  const { trackEvent } = useDatadog();

  const { isBusy, liveTranscript } = useMicRecorder();

  // Reference to the transcript window for auto-scrolling
  const transcriptRef = useRef<HTMLDivElement>(null);

  const MicButton = useMemo(() => {
    return isAudioMuted ? BsVolumeMuteFill : BsVolumeUpFill;
  }, [isAudioMuted]);

  const [showTranscript, setShowTranscript] = useState<boolean>(false);

  // Auto-scroll transcript to bottom when new messages arrive or when transcript becomes visible
  useEffect(() => {
    if ((modusChatContext?.messages?.length || 0) > 1 || liveTranscript) {
      if (transcriptRef.current) {
        transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
      }
    }
  }, [
    latestMessage,
    liveTranscript,
    showTranscript,
    modusChatContext?.messages?.length
  ]);

  const largeSquiglyOffset = size <= 200 ? 0 : 0;
  const largeImgBottom = 60 + largeSquiglyOffset;
  const largeTextBottom = 35 + largeSquiglyOffset;

  const squiglyLine =
    squiglySize === "large" ? (
      <>
        <img
          src={handPaintedArrowShort}
          alt="hand-painted-arrow-short"
          className={`absolute`}
          style={{
            bottom: `${largeImgBottom}px`,
            right: "-55px",
            transform: "rotate(-10deg)",
            width: "75px"
          }}
        />
        <div
          className={`absolute text-center font-semibold`}
          style={{
            bottom: `${largeTextBottom}px`,
            right: "-165px"
          }}
        >
          <p className="mb-0">Just talk.</p>
          <div className="flex items-center justify-center">
            <span>Vo will guide you</span>
            <FaHeart size={15} className="ml-1" />
          </div>
        </div>
      </>
    ) : (
      // <>
      //   <img
      //     src={arrowUp3}
      //     alt="arrow-up-3"
      //     className="absolute bottom-[40px] right-[80px] w-[50px]"
      //   />
      //   <div className="absolute bottom-[10px] right-[-35px] text-center font-semibold">
      //     <p className="mb-0">Just talk.</p>
      //     <div className="flex items-center justify-center">
      //       <span>Vo will guide you</span>
      //       <FaHeart size={15} className="ml-1" />
      //     </div>
      //   </div>
      // </>
      <>
        <img
          src={arrowUp3}
          alt="hand-painted-arrow-short"
          className="absolute"
          style={{
            bottom: `${largeImgBottom - 45}px`,
            right: "-20px",
            width: "50px",
            transform: "rotate(-50deg)"
          }}
        />
        <div
          className="absolute text-center font-semibold"
          style={{
            bottom: "50px",
            right: "-100px",
            fontSize: "13px"
          }}
        >
          <p className="mb-0">Just talk.</p>
          <div className="flex items-center justify-center">
            <span>Vo will guide you</span>
            <FaHeart size={15} className="ml-1" />
          </div>
        </div>
      </>
    );

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <div
        className={styles.modusContainer}
        style={{
          ...style,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          paddingBottom: "24px" // optional, gives it breathing room at the bottom
        }}
      >
        {showTranscript && (
          <>
            {/* Overlay to detect clicks outside the panel */}
            <div
              style={{
                backgroundColor: "#E8E8E8",
                zIndex: 1000,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column"
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px"
                }}
              >
                <Button
                  onClick={() => setShowTranscript(false)}
                  type="text"
                  style={{ fontSize: "16px", marginLeft: "auto" }}
                >
                  ✕
                </Button>
              </div>
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  paddingRight: "10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "28px",
                  paddingTop: "10px",
                  paddingBottom: "20px"
                }}
              >
                {modusChatContext?.messages?.map((message, index) => (
                  <div
                    key={index}
                    style={{
                      maxWidth: "90%",
                      alignSelf:
                        message.role === "user" ? "flex-end" : "flex-start"
                    }}
                  >
                    <div
                      style={{
                        padding: "16px 20px",
                        borderRadius: "8px",
                        backgroundColor:
                          message.role === "user" ? "white" : "transparent",
                        fontSize: "16px",
                        lineHeight: "1.5",
                        color: "black"
                      }}
                    >
                      {message.role === "user" ? (
                        <ReactMarkdown>{message.content}</ReactMarkdown>
                      ) : (
                        (() => {
                          try {
                            const jsonContent = JSON.parse(
                              message.content
                                .replace("json", "")
                                .replaceAll("```", "")
                            );

                            return (
                              jsonContent.final_response || message.content
                            );
                          } catch (e) {
                            return (
                              <ReactMarkdown>{message.content}</ReactMarkdown>
                            );
                          }
                        })()
                      )}
                    </div>
                  </div>
                )) || <div>No conversation history available</div>}
              </div>
            </div>
          </>
        )}
        {!showTranscript && (
          <>
            {headerActionButtons.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: headerActionButtons.length == 2 ? "140px" : "20px",
                  alignItems: "center",
                  marginTop: "30px",
                  marginBottom:
                    headerActionButtons.length == 2 ? "-60px" : "-20px",
                  position: "relative",
                  zIndex: 2
                }}
              >
                {headerActionButtons.map((hab) => (
                  <ActionButton
                    key={hab.text}
                    title={hab.text}
                    action={hab.onClick}
                  />
                ))}
              </div>
            )}

            <div
              style={{
                position: "relative",
                display: "flex",
                marginBottom: showSquigly
                  ? squiglySize === "large"
                    ? "10px"
                    : "20px"
                  : "0px"
              }}
            >
              <ModusOrb
                size={size}
                onProcess={onProcess}
                orbFontSizeOverride={orbFontSizeOverride}
              />
              {/* squigly line */}
              {showSquigly && squiglyLine}
            </div>

            {/* Scrollable messages container */}
            <div
              ref={transcriptRef}
              className={styles.transcriptWindow}
              style={{
                fontSize: fontSizeOverride,
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                padding: "0 20px",
                overflowY: "auto",
                overflowX: "hidden",
                flex: 1,
                minHeight: 0
              }}
            >
              {/* Latest user message or live transcript - right justified (shown first/top) */}
              {(liveTranscript || latestUserMessage?.content) && (
                <div
                  style={{
                    alignSelf: "flex-end",
                    maxWidth: "85%",
                    backgroundColor: "white",
                    padding: "12px 16px",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0"
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontStyle: isBusy ? "italic" : "normal",
                      color: "black",
                      fontWeight: "400",
                      fontSize: fontSizeOverride || "1rem"
                    }}
                  >
                    {liveTranscript || latestUserMessage.content}
                  </p>
                </div>
              )}

              {/* Latest Vo message - left justified, light gray background (shown second/bottom) */}
              {latestVoMessage && modusChatContext?.messages?.length > 0 && (
                <div
                  style={{
                    alignSelf: "flex-start",
                    maxWidth: "85%",
                    backgroundColor: "#f5f5f5",
                    padding: "12px 16px",
                    borderRadius: "8px"
                  }}
                >
                  <div
                    style={{
                      fontSize: fontSizeOverride || "1rem",
                      color: "black"
                    }}
                  >
                    <ReactMarkdown>{latestVoMessage.content}</ReactMarkdown>
                  </div>
                </div>
              )}

              {/* Error message */}
              {latestMessage &&
                latestMessage.role !== "assistant" &&
                !latestUserMessage && (
                  <p style={{ color: "red", textAlign: "center" }}>
                    An error occurred
                    <br />
                    Please try again...
                  </p>
                )}
            </div>
          </>
        )}

        <div
          className="w-[98%] flex items-center"
          style={{ marginTop: "auto", paddingTop: "12px" }}
        >
          {!liveTranscript && !isWaitingForResponse && (
            <>
              <div
                className="flex-grow mr-2"
                style={{ width: "calc(100% - 100px)" }}
              >
                <TypeOrRecord
                  recordingObjectType={RecordingObjectType.MODUS_CHAT}
                  onProcess={onProcess}
                  hideRecordButton={true}
                  placeHolder={placeHolder}
                  rows={4}
                />
              </div>

              {/* Control icons positioned inline with the TypeOrRecord */}
              <div
                className="flex items-center"
                style={{
                  // backgroundColor: "rgb(237, 240, 249)",
                  color: "#586B8F",
                  height: "40px",
                  borderRadius: "30px",
                  padding: "0 8px",
                  flexShrink: 0
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginRight: "8px"
                  }}
                >
                  <Tooltip title={isAudioMuted ? "Unmute" : "Mute"}>
                    <span style={{ display: "inline-flex", cursor: "pointer" }}>
                      <MicButton
                        size={20}
                        color="#35425B"
                        onClick={() => setIsAudioMuted((prev) => !prev)}
                      />
                    </span>
                  </Tooltip>
                </div>

                {showNewConversationButton && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginRight: showTranscriptIcon ? "8px" : "0"
                    }}
                  >
                    <Tooltip title="New conversation">
                      <span style={{ display: "inline-flex", cursor: "pointer" }}>
                        <IoMdAdd
                          size={20}
                          color="#35425B"
                          onClick={() => resetChatContext()}
                        />
                      </span>
                    </Tooltip>
                  </div>
                )}

                {showTranscriptIcon && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginLeft: "8px"
                    }}
                  >
                    <Tooltip
                      title={
                        showTranscript
                          ? "Hide conversation"
                          : "View conversation"
                      }
                      key={showTranscript ? "hide" : "show"}
                    >
                      <span style={{ display: "inline-flex", cursor: "pointer" }}>
                        {showTranscript ? (
                          <BsEyeSlashFill
                            color="#35425B"
                            size={15}
                            onClick={() => {
                              setShowTranscript(false);
                              trackEvent("transcript_hidden");
                            }}
                          />
                        ) : (
                          <BsEyeFill
                            color="#35425B"
                            size={15}
                            onClick={() => {
                              setShowTranscript(true);
                              trackEvent("transcript_visible");
                            }}
                          />
                        )}
                      </span>
                    </Tooltip>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
